import { Checkbox, Form, Input, InputRef, Modal, FormInstance, Select } from 'antd';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './NewRoomModal.module.scss';
import { ChoosePayment } from 'components/TariffsComponent/TariffsComponent';
import { Tariffs } from 'components/TariffsComponent/tariffsData';
import { useDebounce } from 'use-debounce';
import billing from 'api/billing';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import {
  handleInputChangeWithAltFilter,
  handlePasteWithAltFilter,
} from 'helpers/filterAltCodes';

export type PaymentOption = 'recurring' | 'single';

export interface NewRoomFormValues {
  name: string;
  description?: string;
  quote_name?: string;
  payment_option?: PaymentOption;
  is_accepted_agreement?: boolean;
  allow_recurring_payments?: boolean;
  is_accepted_public_offer?: boolean;
}

type BuyTariffModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  onTransferToNewQuote: () => void;
  isCreatingRoom: boolean;
  form: FormInstance;
  nameInput: React.RefObject<InputRef>;
  fields: { name: string; description: string };
  onInputsChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onInputsPaste: (
    event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  newRoomState: ChoosePayment;
  ended_tariff_lifetime: string | null;
  nameOfExpiredRoom: string | undefined;
  openLicenseModal: (e: React.MouseEvent<HTMLSpanElement>) => void;
  openOfferModal: (e: React.MouseEvent<HTMLSpanElement>) => void;
  modalTitle: (value?: Tariffs) => string;
  roomNameLabel: (value?: Tariffs) => string;
  period: string | undefined;
  mode?: 'single' | 'quota';
};

export default function BuyTariffModal({
  isOpen,
  onCancel,
  onSubmit,
  onTransferToNewQuote,
  isCreatingRoom,
  form,
  nameInput,
  fields,
  onInputsChange,
  onInputsPaste,
  newRoomState,
  ended_tariff_lifetime,
  nameOfExpiredRoom,
  openLicenseModal,
  openOfferModal,
  modalTitle,
  roomNameLabel,
  period,
  mode,
}: BuyTariffModalProps) {
  const { t } = useTranslation();

  const [quoteNameError, setQuoteNameError] = useState<string>('');
  const [isValidatingQuoteName, setIsValidatingQuoteName] = useState<boolean>(false);
  
  const quoteName = Form.useWatch('quote_name', form);
  const [debouncedQuoteName] = useDebounce(quoteName, 500);

  useEffect(() => {
    const validateQuoteName = async () => {
      if (mode === 'quota' && debouncedQuoteName && newRoomState?.tariff?.id) {
        setIsValidatingQuoteName(true);
        try {
          const response = await billing.validateQuoteName(debouncedQuoteName, newRoomState.tariff.id);
          if (response.data.exists) {
            setQuoteNameError(t('Header.createModal.quoteNameExists'));
          } else {
            setQuoteNameError('');
          }
        } catch {
          setQuoteNameError('');
        } finally {
          setIsValidatingQuoteName(false);
        }
      } else if (mode === 'quota' && !debouncedQuoteName) {
        setQuoteNameError('');
      }
    };

    validateQuoteName();
  }, [debouncedQuoteName, newRoomState?.tariff?.id, mode, t]);

  const paymentOption: PaymentOption = Form.useWatch<PaymentOption>('payment_option', form) || 'recurring';
  const isAcceptedAgreement = Form.useWatch('is_accepted_agreement', form);
  const isAllowRecurringPayments = Form.useWatch('allow_recurring_payments', form);
  const isAcceptedPublicOffer = Form.useWatch('is_accepted_public_offer', form);

  const isAcceptedAllCheckboxes = paymentOption === 'recurring'
    ? (isAcceptedAgreement && isAllowRecurringPayments && isAcceptedPublicOffer)
    : isAcceptedAgreement

  const isButtonDisabled = !isAcceptedAllCheckboxes || (mode === 'quota' && !!quoteNameError);

  const onQuoteNameChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    handleInputChangeWithAltFilter(event, (filteredValue) => {
      form.setFieldValue('quote_name', filteredValue);
    });
  };

  const onQuoteNamePaste = (
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const currentValue = form.getFieldValue('quote_name') || '';
    handlePasteWithAltFilter(event, currentValue, (filteredValue) => {
      form.setFieldValue('quote_name', filteredValue);
    });
  };  

  return (
    <Modal
      styles={{ body: { maxHeight: '85vh', padding: '20px 15px 20px 20px' } }}
      width={'580px'}
      title={modalTitle(newRoomState?.tariff?.tariff_name)}
      open={isOpen}
      centered={true}
      mask={{ closable: false }}
      onCancel={onCancel}
      onOk={ended_tariff_lifetime ? onTransferToNewQuote : form.submit}
      cancelButtonProps={{ style: { display: 'none' } }}
      confirmLoading={isCreatingRoom}
      className={cn(classes.modalAddRoom, getTheme())}
      okButtonProps={{
        disabled: isButtonDisabled,
        style: { width: '100%', margin: 0 },
      }}
      okText={t('Header.createModal.payButton')}
    >
      <div className={classes.modalWrap}>
        <Form
          className={classes.form}
          layout='vertical'
          onFinish={onSubmit}
          form={form}
        >
          {ended_tariff_lifetime ? (
            <div className={classes.paymentInfoCard}>
              <div className={classes.infoRow}>
                <div className={classes.label}>
                  {t('Header.createModal.payForTariff')}
                </div>
                <div className={classes.value}>
                  {modalTitle(newRoomState?.tariff?.tariff_name)}
                </div>
              </div>
              <div className={classes.infoRow}>
                <div className={classes.label}>
                  {t('Header.createModal.forRoom')}
                </div>
                <div className={classes.value}>
                  {nameOfExpiredRoom}
                </div>
              </div>
            </div>
          ) : mode === 'single' ? (
            <>
              <Form.Item
                className={classes.formInput}
                label={roomNameLabel(newRoomState?.tariff?.tariff_name)}
                name='name'
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('Header.createModal.roomNameValidate'),
                  },
                  {
                    pattern: /^(?!.*\s{2,}).*$/,
                    message: t('Header.createModal.multipleSpacesError'),
                  },
                ]}
              >
                <Input
                  ref={nameInput}
                  count={{
                    show: true,
                    max: 80,
                  }}
                  name='name'
                  value={fields.name}
                  onChange={onInputsChange}
                  onPaste={onInputsPaste}
                  maxLength={80}
                  placeholder={t('Header.createModal.namepPaceholder')}
                />
              </Form.Item>

              <Form.Item
                label={t('Settings.room.labels.description')}
                name='description'
              >
                <Input.TextArea
                  placeholder={t('Settings.room.placeholders.description')}
                  name='description'
                  autoSize={{ minRows: 4, maxRows: 4 }}
                  value={fields.description}
                  onChange={onInputsChange}
                  onPaste={onInputsPaste}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              className={classes.form_input}
              label={t('Header.createModal.quoteNameLabel')}
              name='quote_name'
              validateStatus={quoteNameError ? 'error' : isValidatingQuoteName ? 'validating' : ''}
              help={quoteNameError}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t('Header.createModal.quoteNameValidate'),
                },
                {
                  pattern: /^(?!.*\s{2,}).*$/,
                  message: t('Header.createModal.multipleSpacesError'),
                },
              ]}
            >
              <Input
                ref={nameInput}
                count={{
                  show: true,
                  max: 80,
                }}
                name='quote_name'
                maxLength={80}
                placeholder={t('Header.createModal.quoteNamePlaceholder')}
                onChange={onQuoteNameChange}
                onPaste={onQuoteNamePaste}
              />
            </Form.Item>
          )}

          {newRoomState?.tariff?.tariff_name !== Tariffs.Trial && (
            <Form.Item
              className={classes.formInput}
              label={t('Header.createModal.paymentMethodLabel')}
              name='payment_option'
              initialValue='recurring'
            >
              <Select
                options={[
                  {
                    value: 'recurring',
                    label: t('Header.createModal.paymentOptionRecurring'),
                  },
                  {
                    value: 'single',
                    label: t('Header.createModal.paymentOptionSingle'),
                  },
                ]}
              />
            </Form.Item>
          )}

          {newRoomState?.tariff && (
            <div className={classes.productCard}>
              <div className={classes.title}>
                {t('Header.createModal.productCard.title')}
              </div>
              <div className={classes.row}>
                <div className={classes.name}>
                  {t('Header.createModal.productCard.tariffName')}
                </div>
                <div className={classes.text}>
                  {t(`Tariffs.tariff_name.${newRoomState.tariff.tariff_name}`)}
                </div>
              </div>
              {newRoomState.tariff.tariff_name !== Tariffs.Trial && (
                <>
                  <div className={classes.row}>
                    <div className={classes.name}>
                      {t('Header.createModal.productCard.tariffCost')}
                    </div>
                    <div className={classes.text}>
                      {paymentOption === 'recurring'
                        ? `${newRoomState.tariff.tariff_cost.toLocaleString()} ${newRoomState.tariff.tariff_currency} ${t('Tariffs.price')}`
                        : `${newRoomState.tariff.tariff_cost.toLocaleString()} ${newRoomState.tariff.tariff_currency} ${t(`Tariffs.one_${period}`)}`}
                    </div>
                  </div>
                  <div className={classes.row}>
                    <div className={classes.name}>
                      {t('Header.createModal.paymentTermsLabel')}
                    </div>
                    <div className={classes.text}>
                      {paymentOption === 'recurring'
                        ? t('Header.createModal.recurringTermsDesc')
                        : t('Header.createModal.singleTermsDesc')}
                    </div>
                  </div>
                </>
              )}
              <div className={classes.row}>
                <div className={classes.name}>
                  {t('Header.createModal.productCard.tariffInfo')}
                </div>
                <div className={classes.text}>
                  <span>{`- ${t('Header.createModal.productCard.storage')} ${
                    newRoomState.tariff.tariff_constraints.storage
                  } ${t('Tariffs.gb')}`}</span>
                  <br />
                  <span className={classes.count}>
                    -{' '}
                    {t('Tariffs.participants_count', {
                      count: newRoomState.tariff.tariff_constraints.members,
                    })}
                  </span>
                </div>
              </div>
              <div></div>
            </div>
          )}

          {paymentOption === 'recurring' && (
            <Form.Item
              name='allow_recurring_payments'
              valuePropName='checked'
              className={classes.formCheck}
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('PersonalInfo.allowedRecurringPaymentsError'))),
                },
              ]}
            >
              <Checkbox className={classes.checkboxStyle}>
                {t('PersonalInfo.allowRecurringPayments')}
              </Checkbox>
            </Form.Item>
          )}
          { paymentOption === 'recurring' && (
            <Form.Item
              name='is_accepted_public_offer'
              valuePropName='checked'
              style={{ marginTop: -15 }}
              className={classes.formCheck}
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('PersonalInfo.acceptedPublicOfferError'))),
                },
              ]}
            >
              <Checkbox className={classes.checkboxStyle}>
                <span className={classes.linkButton}>
                  {t('PersonalInfo.acceptedPublicOffer')}{' '}
                  <span
                    className={classes.link}
                    onClick={(e) => openOfferModal(e)}
                  >
                    {t('PersonalInfo.publicOffer')}
                  </span>{' '}
                  {t('PersonalInfo.acceptedPublicOfferEnd')}
                </span>
                
              </Checkbox>
            </Form.Item>
          )}
          <Form.Item
            name='is_accepted_agreement'
            valuePropName='checked'
            className={classes.formCheck}
            style={paymentOption === 'recurring' ? { marginTop: -15 } : {}}
            // отменено в рамках задачи №19162
            // getValueFromEvent={(e) => {
            //   if (e.target.checked) openLicenseModal(e);
            // }}
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('PersonalInfo.acceptedLicenseError'))),
              },
            ]}
          >
            <Checkbox className={classes.checkboxStyle}>
              <span className={classes.linkButton}>
                {t('PersonalInfo.acceptAgreement')}{' '}
                <span
                  className={classes.link}
                  onClick={(e) => openLicenseModal(e)}
                >
                  {t('PersonalInfo.license')}
                </span>{' '}
                {t('PersonalInfo.platform')}
              </span>
            </Checkbox>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}
